import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  SignOut,
  Sparkle,
  Newspaper,
  Users,
  ChartLineUp,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="border border-foreground/15 p-6 bg-card">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon size={18} className="text-primary" />}
      </div>
      <div className="font-display font-black text-4xl md:text-5xl mt-3 tracking-tight">
        {value ?? "—"}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [latest, setLatest] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.stats(), api.articles({ limit: 8 })]);
      setStats(s);
      setLatest(l);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[AdminDashboard] Failed to load stats/articles:", e);
      }
      toast.error("Failed to load dashboard data. Refresh to retry.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const onGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      await api.adminGenerateNow();
      toast.success("Writer started. New article will appear in ~60 seconds.");
      // refresh stats after a moment
      setTimeout(load, 65000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Failed to trigger writer";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <SEO title="Admin dashboard" description="Finance Topia editorial admin dashboard" url="/admin" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Admin" }]} />
      </div>

      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b-2 border-foreground pb-8 flex items-start justify-between gap-6">
        <div>
          <div className="overline text-primary">Editorial Desk</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tight mt-2">
            Admin<span className="italic font-normal">.</span>
          </h1>
          <p className="mt-3 text-foreground/70 text-sm font-mono">
            Signed in as <strong className="text-foreground">{user?.email}</strong>
          </p>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 border border-foreground/20 h-10 px-4 text-sm hover:bg-foreground hover:text-background transition-colors duration-200"
        >
          <SignOut size={16} weight="bold" /> Sign out
        </button>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid gap-6 md:grid-cols-3">
        <StatCard testid="stat-articles" label="Articles" value={stats?.articles} icon={Newspaper} />
        <StatCard testid="stat-subs" label="Subscribers" value={stats?.subscribers} icon={Users} />
        <StatCard
          testid="stat-cats"
          label="Categories"
          value={stats ? Object.keys(stats.per_category).length : null}
          icon={ChartLineUp}
        />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 grid gap-6 md:grid-cols-2">
        <div className="border border-foreground/15 p-6 bg-card">
          <div className="overline mb-3">Actions</div>
          <h2 className="font-display font-black text-2xl mb-4 tracking-tight">
            Trigger the writer
          </h2>
          <p className="text-sm text-foreground/70 mb-5 leading-relaxed">
            Publish one new AI-authored article immediately, on top of the scheduled 3-day cadence. Uses Claude Sonnet 4.6 — takes ~60 seconds.
          </p>
          <button
            onClick={onGenerate}
            disabled={generating}
            data-testid="admin-generate-now"
            className="inline-flex items-center gap-2 h-12 px-6 bg-foreground text-background font-semibold hover:bg-primary transition-colors duration-200 disabled:opacity-60"
          >
            <Sparkle size={16} weight="bold" />
            {generating ? "Writing…" : "Generate one article now"}
          </button>
        </div>

        <div className="border border-foreground/15 p-6 bg-card">
          <div className="overline mb-3">Category breakdown</div>
          <h2 className="font-display font-black text-2xl mb-4 tracking-tight">
            Articles per desk
          </h2>
          <div className="space-y-2">
            {stats?.per_category &&
              Object.entries(stats.per_category)
                .sort((a, b) => b[1] - a[1])
                .map(([slug, count]) => (
                  <div
                    key={slug}
                    data-testid={`admin-cat-${slug}`}
                    className="flex items-baseline justify-between py-1.5 border-b border-foreground/10 last:border-b-0"
                  >
                    <Link
                      to={`/category/${slug}`}
                      className="text-sm font-medium hover:text-primary transition-colors duration-200"
                    >
                      {slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </Link>
                    <span className="font-mono text-sm text-foreground/70">{count}</span>
                  </div>
                ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="border-b-2 border-foreground pb-3 mb-4 flex items-baseline justify-between">
          <h2 className="font-display font-black text-2xl">Latest articles</h2>
          <span className="font-mono text-xs text-foreground/50">{latest.length} shown</span>
        </div>
        <div className="border border-foreground/15">
          {latest.map((a) => (
            <Link
              key={a.id}
              to={`/article/${a.slug}`}
              data-testid={`admin-latest-${a.slug}`}
              className="flex items-center gap-4 px-4 py-3 border-b border-foreground/10 last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
            >
              <img src={a.cover_image} alt="" className="w-16 h-16 object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="overline text-primary text-[10px]">{a.category_name}</div>
                <div className="font-display font-bold text-sm md:text-base leading-snug mt-1 line-clamp-2">
                  {a.title}
                </div>
              </div>
              <div className="text-[11px] font-mono text-foreground/50 whitespace-nowrap">
                {new Date(a.published_at).toLocaleDateString()}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
